/**
 * Material Section: show Component Details icon when business vertical matches
 * Mikasa Door(s), Mikasa Floor, Greenlam Cubicle (Sturdo), or standalone Sturdo.
 * Uses case-insensitive comparison and common label variants from SAP/master data.
 */
export function materialRowShowsComponentDetailsIcon(row) {
  const raw = getBusinessVerticalFromRow(row);
  if (!raw) return false;
  return (
    matchesSturdoBusinessVertical(raw) ||
    matchesMikasaDoorsBusinessVertical(raw) ||
    matchesMikasaFloorsBusinessVertical(raw)
  );
}

export function getBusinessVerticalFromRow(row) {
  return (
    row?.BusinessVertical ??
    row?.businessVertical ??
    row?.BVName ??
    row?.bvName ??
    ""
  )
    .toString()
    .trim();
}

function normalizeBvText(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/** Sturdo / Greenlam Cubicle (Sturdo) — excludes Non-Sturdo. */
export function matchesSturdoBusinessVertical(text) {
  const n = normalizeBvText(text);
  if (!n) return false;
  if (n.includes("non-sturdo") || n.includes("non sturdo")) return false;
  if (n === "sturdo") return true;
  const cubicleNorm = n.replace(/\(\s*/g, "(").replace(/\s*\)/g, ")");
  if (cubicleNorm === "greenlam cubicle (sturdo)") return true;
  if (n.includes("greenlam") && n.includes("cubicle") && n.includes("sturdo")) return true;
  return n.includes("sturdo");
}

/** Mikasa Door(s) — excludes Floors / Ply / Veneer. */
export function matchesMikasaDoorsBusinessVertical(text) {
  const raw = normalizeBvText(text);
  if (!raw || raw.includes("veneer")) return false;
  if (raw.includes("floor") && raw.includes("mikasa")) return false;
  if (raw.includes("ply") && raw.includes("mikasa") && !raw.includes("door")) return false;
  const normalized = raw.replace(/[^a-z0-9]/g, "");
  if (
    normalized === "mikasadoor" ||
    normalized === "mikasadoors" ||
    normalized.includes("mikasadoor")
  ) {
    return true;
  }
  return raw.includes("mikasa") && raw.includes("door");
}

/** Mikasa Floor(s) — excludes Ply / Veneer. */
export function matchesMikasaFloorsBusinessVertical(text) {
  const raw = normalizeBvText(text);
  if (!raw || raw.includes("veneer")) return false;
  if (raw.includes("mikasa") && raw.includes("ply") && !raw.includes("floor")) return false;
  const normalized = raw.replace(/[^a-z0-9]/g, "");
  if (normalized === "mikasafloors" || normalized.includes("mikasafloor")) return true;
  return raw.includes("mikasa") && raw.includes("floor");
}

function materialGroupName(option) {
  return String(option?.name ?? option?.Name ?? "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Material Group Desc. dropdown filter by business vertical:
 * - Sturdo → STURDO CUBICLE / UMP / LOCKERS
 * - Mikasa Doors → MIKASA FRAME / LEAF / SET (Door)
 * - Mikasa Floors → FLOORING
 * Other / empty BV → unfiltered list.
 */
export function filterMaterialGroupsByBusinessVertical(options, businessVertical) {
  const list = Array.isArray(options) ? options : [];
  if (!businessVertical) return list;

  if (matchesSturdoBusinessVertical(businessVertical)) {
    return list.filter((o) => materialGroupName(o).includes("sturdo"));
  }

  if (matchesMikasaDoorsBusinessVertical(businessVertical)) {
    return list.filter((o) => {
      const n = materialGroupName(o);
      if (!n.includes("mikasa") || n.includes("floor")) return false;
      return (
        n.includes("frame") ||
        n.includes("leaf") ||
        n.includes("door") ||
        n.includes("set")
      );
    });
  }

  if (matchesMikasaFloorsBusinessVertical(businessVertical)) {
    return list.filter((o) => {
      const n = materialGroupName(o);
      return n === "flooring" || (n.includes("floor") && !n.includes("door"));
    });
  }

  return list;
}
